import React, { useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import BaseTextField from "../../../components/ui/BaseTextField";
import { useFormik } from "formik";
import * as yup from "yup";
import BaseButton from "../../../components/ui/BaseButton";
import { useMutation } from "@tanstack/react-query";
import { changePassword } from "../../../services/api/auth";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";

const validationSchema = yup.object({
  oldPassword: yup.string().required("Old Password is required"),
  newPassword: yup
    .string()
    .required("New Password is required")
    .min(8, "Password must be at least 8 characters long")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[@$!%*?&]/,
      "Password must contain at least one special character (@, $, !, %, *, ?, &)",
    )
    .test(
      "no-spaces",
      "Password cannot contain spaces",
      (value) => !/\s/.test(value), // Rejects passwords with spaces
    )
    .notOneOf([yup.ref("oldPassword")], "New Password must be different from the Old Password"),
  confirmPassword: yup
    .string()
    .required("Confirm Password is required")
    .oneOf([yup.ref("newPassword")], "Passwords must match"),
});
const ResetPassword = ({ onNewPasswordChange }) => {
  const dispatch = useDispatch();
  const [isShowOldPassword, setIsShowOldPassword] = useState(false);
  const [isShowNewPassword, setIsShowNewPassword] = useState(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false);
  const {
    isPending: isChangePasswordApiLoading,
    error: isChangePasswordApiError,
    mutateAsync: changePasswordMutation,
  } = useMutation({
    mutationFn: changePassword,
  });

  const formik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      changePasswordButtonClicked(values);
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const changePasswordButtonClicked = async (payload) => {
    const { newPassword, oldPassword } = payload;
    try {
      const changePasswordResponse = await changePasswordMutation({
        newPassword,
        oldPassword,
      });
      formik.resetForm();
      onNewPasswordChange?.("");
      dispatch(
        showToast({
          style: {
            width: "350px",
          },
          position: "top-center",
          type: "success",
          message: changePasswordResponse?.message || "Password changed successfully",
        }),
      );
    } catch (error) {
      const serverMessage = error?.response?.data?.message;
      // The API answers a bad current password with a bare "Wrong password" -
      // say which field it refers to, and flag that field inline.
      const isWrongOldPassword = /wrong|incorrect|invalid/i.test(serverMessage || "");

      if (isWrongOldPassword) {
        formik.setFieldTouched("oldPassword", true, false);
        formik.setFieldError("oldPassword", "Old Password is incorrect");
      }

      dispatch(
        showToast({
          style: {
            width: "350px",
          },
          position: "top-center",
          type: "error",
          message: isWrongOldPassword
            ? "Old Password is incorrect"
            : serverMessage || "Failed to change password. Please try again.",
        }),
      );
    }
  };

  return (
    <form>
      <BaseCard className="py-2 px-4 sm:px-6 rounded-lg" elevation={1}>
        <div className="bg-light px-4 py-2 mt-2 mb-2 rounded-md flex justify-between items-center">
          <h6 className="text-dark font-normal">Change Password</h6>
        </div>
        {/* Inset to line up with the header's title, as the policy list above does. */}
        <div className="px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mt-4">
            <div>
              <BaseTextField
                type={isShowOldPassword ? "text" : "password"}
                endAdornment={
                  <i
                    className={`fa-solid ${
                      isShowOldPassword ? "fa-eye" : "fa-eye-slash"
                    } text-base cursor-pointer text-gray-400`}
                    onClick={() => setIsShowOldPassword(!isShowOldPassword)}
                  ></i>
                }
                label="Old Password"
                labelShrink={false}
                optional={false}
                name="oldPassword"
                value={formik.values.oldPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.oldPassword && Boolean(formik.errors.oldPassword)}
                helperText={formik.touched.oldPassword && formik.errors.oldPassword}
              />
            </div>
            <div>
              <BaseTextField
                type={isShowNewPassword ? "text" : "password"}
                endAdornment={
                  <i
                    className={`fa-solid ${
                      isShowNewPassword ? "fa-eye" : "fa-eye-slash"
                    } text-base cursor-pointer text-gray-400`}
                    onClick={() => setIsShowNewPassword(!isShowNewPassword)}
                  ></i>
                }
                label="New Password"
                labelShrink={false}
                optional={false}
                name="newPassword"
                value={formik.values.newPassword}
                onChange={(event) => {
                  formik.handleChange(event);
                  onNewPasswordChange?.(event.target.value);
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                helperText={formik.touched.newPassword && formik.errors.newPassword}
              />
            </div>
            <div>
              <BaseTextField
                type={isShowConfirmPassword ? "text" : "password"}
                endAdornment={
                  <i
                    className={`fa-solid ${
                      isShowConfirmPassword ? "fa-eye" : "fa-eye-slash"
                    } text-base cursor-pointer text-gray-400`}
                    onClick={() => setIsShowConfirmPassword(!isShowConfirmPassword)}
                  ></i>
                }
                label="Confirm Password"
                labelShrink={false}
                optional={false}
                name="confirmPassword"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
              />
            </div>
          </div>
          <BaseButton
            color="primary"
            className="py-[6px] px-[8px] my-4"
            isLoading={isChangePasswordApiLoading}
            loaderSize="22px"
            disabled={!formik.dirty || !formik.isValid || isChangePasswordApiLoading}
            onClick={formik.handleSubmit}
          >
            Change Password
          </BaseButton>
        </div>
      </BaseCard>
    </form>
  );
};

export default ResetPassword;
